<template>
  <a-modal v-if="visible" title="Filters" width="600px" :visible="visible" @cancel="cancel">
    <div class="grid">
      <b class="col-4">Start Date</b>
      <a-date-picker
        class="col-8"
        format="MM/DD/YYYY"
        v-model="filters.start"
        valueFormat="YYYY-MM-DD"
        placeholder="Start Date"
        @change="handleStartDateChange('start', 'end')"
      />

      <b class="col-4">End Date</b>
      <a-date-picker
        class="col-8"
        format="MM/DD/YYYY"
        v-model="filters.end"
        valueFormat="YYYY-MM-DD"
        placeholder="End Date"
        ref="endDatePicker"
        @change="handleEndDateChange('start', 'end')"
        :open="isEndDatePickerOpen"
        :default-picker-value="getEndDateDefaultPickerValue('start')"
        @openChange="handleEndDateOpenChange"
      />

      <b class="col-4">Payroll Violations</b>
      <custom-select
        class="col-8"
        :multiple="true"
        :value.sync="payrollSelected"
        placeholder="Type"
        :options="payrollAuths"
        name="name"
        optionKey="key"
      />

      <b class="col-4">Disciplinary Violations</b>
      <custom-select
        class="col-8"
        :multiple="true"
        :value.sync="disciplinarySelected"
        placeholder="Type"
        :options="disciplinaryAuths"
        name="name"
        optionKey="key"
      />

      <b class="col-4">Violation Status</b>
      <custom-select
        class="col-8"
        :multiple="true"
        :value.sync="filters.auth_statuses"
        placeholder="Status"
        :options="authStatuses"
        name="name"
        optionKey="id"
      />

      <b class="col-4">Provider</b>
      <search-associates class="col-8" :is-associate-id="true" :multiply="true" :value.sync="filters.providers" />

      <b class="col-4">Reviewer</b>
      <search-associates class="col-8" :is-associate-id="true" :multiply="true" :value.sync="filters.reviewers" />

      <span class="col-12"></span>

      <b class="col-4">Shift Type</b>
      <a-select
        class="col-8"
        v-model="filters.shift_type"
        :allow-clear="true"
        :dropdown-match-select-width="false"
        placeholder="Shift Type"
      >
        <a-select-option value="worked"> Worked </a-select-option>
        <a-select-option value="pay_code"> Pay Code </a-select-option>
      </a-select>

      <template v-if="filters.shift_type === 'worked'">
        <b class="col-4">With attached pay codes</b>
        <a-checkbox class="col-8" v-model="filters.attached_pay_code" />
      </template>

      <b class="col-4">Shift Statuses</b>
      <custom-select
        class="col-8"
        :multiple="true"
        :value.sync="filters.shift_statuses"
        placeholder="Status"
        :options="shiftStatuses"
        name="title"
        optionKey="key"
      />

      <b class="col-4">Payment Statuses</b>
      <custom-select
        class="col-8"
        :multiple="true"
        :value.sync="filters.shift_payment_statuses"
        placeholder="Status"
        :options="paymentStatuses"
        name="title"
        optionKey="key"
      />

      <b class="col-4">Origins</b>
      <custom-select
        class="col-8"
        :multiple="true"
        :value.sync="filters.origins"
        placeholder="Origins"
        :options="origins"
        name="title"
        optionKey="key"
      />

      <div class="flex gap-2 flex-wrap mt-2">
        <template v-if="(filters.auth_statuses ?? []).includes('declined')">
          <a-button
            @click="
              () => {
                filters.resolved_by_associate = !JSON.parse(filters.resolved_by_associate)
              }
            "
            :type="
              filters.resolved_by_associate !== undefined
                ? JSON.parse(filters.resolved_by_associate)
                  ? 'primary'
                  : 'default'
                : 'default'
            "
          >
            Resolved By Associate
          </a-button></template
        >
        <a-button @click="toggleFilter('assigned_to_me')" :type="filters.assigned_to_me ? 'primary' : 'default'">
          Assigned to Me
        </a-button>
        <a-button @click="toggleFilter('manually_created')" :type="filters.manually_created ? 'primary' : 'default'">
          Manually Created
        </a-button>
        <a-button @click="toggleFilter('adjusted')" :type="filters.adjusted ? 'primary' : 'default'">
          Adjusted
        </a-button>
      </div>
    </div>
    <template slot="footer">
      <div class="flex justify-content-end">
        <a-button type="danger" :disabled="isEmptyFilters" @click="reset"> Reset </a-button>
        <a-button type="primary" @click="apply"> Apply </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { Modal, Button, Select, Checkbox, DatePicker } from 'ant-design-vue'
import {
  AUTH_PROCESS_STATUSES,
  SHIFT_ORIGIN,
  SHIFT_PAYMENT_REQUEST_STATUS,
  SHIFT_STATUS
} from '@/store/modules/shifts/constants'
import CustomSelect from '@/components/ui/custom-select.vue'
import { PAYMENT_WORK_STATUS } from '@/store/modules/payment/constants'
import { deepCopy } from '@/utils/deep-copy'
import SearchAssociates from '@/components/search-associates.vue'
import LinkedDateFilterMixin from '@/mixins/linkedDateFilterMixin'

const filtersBaseState = Object.freeze({
  end: undefined,
  start: undefined,
  auth_types: [],
  providers: [],
  reviewers: [],
  assigned_to_me: false,
  manually_created: undefined,
  adjusted: undefined,
  auth_statuses: undefined,
  origins: undefined,
  shift_type: undefined,
  shift_statuses: undefined,
  attached_pay_code: undefined,
  resolved_by_associate: undefined
})

export default {
  mixins: [LinkedDateFilterMixin],
  components: {
    'a-modal': Modal,
    'a-button': Button,
    'a-select': Select,
    'a-checkbox': Checkbox,
    'a-select-option': Select.Option,
    'a-date-picker': DatePicker,
    'custom-select': CustomSelect,
    'search-associates': SearchAssociates
  },
  data() {
    return {
      filters: { ...filtersBaseState },

      shiftStatuses: Object.entries(SHIFT_STATUS).map(([key, value]) => ({
        key,
        ...value
      })),

      paymentStatuses: Object.entries(SHIFT_PAYMENT_REQUEST_STATUS).map(([key, value]) => ({
        key,
        ...value
      })),

      authStatuses: Object.entries(AUTH_PROCESS_STATUSES).map(([key, value]) => ({
        id: key,
        name: value
      })),

      payStatuses: Object.entries(PAYMENT_WORK_STATUS).map(([key, value]) => ({
        key,
        value
      })),
      origins: Object.entries(SHIFT_ORIGIN).map(([key, value]) => ({
        key,
        ...value
      }))
    }
  },
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    currentFilters: {
      type: Object,
      required: true
    }
  },
  computed: {
    mappedAuths() {
      return this.$store.state.applicationState.extendedShiftAuthTypes
    },

    disciplinaryAuths() {
      return this.mappedAuths.filter(item => item.auth_type === 'disciplinary')
    },

    payrollAuths() {
      return this.mappedAuths.filter(item => item.auth_type === 'payroll')
    },

    isEmptyFilters() {
      return JSON.stringify(filtersBaseState) === JSON.stringify(this.filters)
    },

    payrollSelected: {
      get() {
        return (this.filters.auth_types ?? []).filter(key => this.payrollAuths.some(auth => auth.key === key))
      },
      set(values) {
        this.filters.auth_types = [...values, ...this.disciplinarySelected]
      }
    },

    disciplinarySelected: {
      get() {
        return (this.filters.auth_types ?? []).filter(key => this.disciplinaryAuths.some(auth => auth.key === key))
      },
      set(values) {
        this.filters.auth_types = [...values, ...this.payrollSelected]
      }
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          const currentFilters = deepCopy(this.currentFilters)

          this.filters = {
            ...filtersBaseState,
            ...currentFilters,
            attached_pay_code:
              currentFilters.attached_pay_code === undefined ? undefined : JSON.parse(currentFilters.attached_pay_code)
          }
        }
      },
      immediate: true
    },
    'filters.shift_type': {
      handler(newVal) {
        if (newVal === 'worked') {
          this.filters.attached_pay_code = false
        } else {
          this.filters.attached_pay_code = undefined
        }
      }
    },
    'filters.auth_statuses': {
      handler(newVal) {
        if (!(newVal ?? []).includes('declined')) {
          this.filters.resolved_by_associate = undefined
        } else if (!this.filters.resolved_by_associate && newVal.includes('declined')) {
          this.filters.resolved_by_associate = false
        }
      }
    }
  },
  methods: {
    reset() {
      if (this.isEmptyFilters) {
        this.cancel()
      } else {
        this.filters = deepCopy(filtersBaseState)
        this.$emit('update:currentFilters', this.filters)
      }
    },

    toggleFilter(type) {
      this.filters[type] = this.filters[type] ? undefined : true
    },

    cancel() {
      this.$emit('update:visible', false)
    },

    apply() {
      this.cancel()

      this.$emit('update:currentFilters', this.filters)
    }
  }
}
</script>

<style scoped>
.ant-calendar-picker {
  margin: 0;
}
</style>
